import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useEnabledQuery } from '../../../../ui/hooks/useEnabledQuery';
import { getMutationSchema } from '../../../../ui/util/getMutationSchema';
import { ResourceType } from '../../../enumerations/ResourceType';
import { get } from '../../../resource-base/APIVersion1';
import { useIndirectlyVersionedResourceAdapter } from '../../../resource-base/adapters/useIndirectlyVersionedResourceAdapter';
import { useMultiResourceSingleParentedResourceAdapterActions } from '../../../resource-base/adapters/useMultiResourceSingleParentedResourceAdapterActions';
import {
    defaultIndirectlyVersionedAndNamedArtifactResource,
    IndirectlyVersionedAndNamedArtifactResourceSchema,
} from '../../../resource-base/artifactResourceTypes';
import { ParamConstants } from '../../../resource-base/types';

export enum UseCasePropertyValidation {
    NAME = 'NAME',
    DESCRIPTION = 'DESCRIPTION',
}

export const UseCaseResourceSchema = IndirectlyVersionedAndNamedArtifactResourceSchema.extend({
    resourceType: z.literal(ResourceType.USE_CASE).default(ResourceType.USE_CASE),
    description: z.string(),
});
export const UseCaseMutationResourceSchema = getMutationSchema(UseCaseResourceSchema);

export type UseCaseResource = z.infer<typeof UseCaseResourceSchema>;

export const defaultUseCaseResource: UseCaseResource = {
    ...defaultIndirectlyVersionedAndNamedArtifactResource,
    resourceType: ResourceType.USE_CASE,
    description: '',
};

export function useUseCaseActions() {
    const getEnabled = useEnabledQuery();
    const actions = {
        ...useMultiResourceSingleParentedResourceAdapterActions(
            'project/use-case',
            ResourceType.USE_CASE,
            UseCaseResourceSchema
        ),
        ...useIndirectlyVersionedResourceAdapter('project/use-case', ResourceType.USE_CASE, UseCaseResourceSchema),
    };

    const useGetAllUseCasesByStandardAssociationAndProjectVersion = (
        projectVersionId: string | undefined | null,
        standardObjectId: string | undefined | null
    ) => {
        return useQuery({
            queryKey: [projectVersionId, standardObjectId, ResourceType.USE_CASE],
            queryFn: () =>
                get(
                    `project/use-case/by-standard?${ParamConstants.PARENT_ID_REQUEST_PARAM_NAME}=${projectVersionId}&standard=${standardObjectId}`
                ).then((result) => actions.processGetCollectionResponse<UseCaseResource>(result)),
            enabled: getEnabled(projectVersionId && standardObjectId),
        });
    };

    return {
        ...actions,
        useGetAllUseCasesByStandardAssociationAndProjectVersion,
    };
}
